
export interface FaceHandMetrics {
  lipCurveSmile: number;
  lipCurveSad: number;
  mar: number;
  ear: number;
  handNearMouth: boolean;
}

export const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

export const avgKeys = (bs: Record<string, number>, keys: string[]): number => {
  let sum = 0;
  let count = 0;
  for (const k of keys) {
    if (bs[k] !== undefined) {
      sum += bs[k];
      count++;
    }
  }
  return count > 0 ? sum / count : 0;
};

const dist = (a: { x: number; y: number }, b: { x: number; y: number }): number =>
  Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

export const computeFaceAndHandMetrics = (
  landmarks?: Array<{ x: number; y: number; z: number }>,
  handLandmarks?: Array<{ x: number; y: number; z?: number }>,
  blendshapes?: Record<string, number>,
  headRotation?: { yaw: number; pitch: number; roll: number },
): FaceHandMetrics => {
  let lipCurveSmile = 0;
  let lipCurveSad = 0;
  let mar = 0;
  let ear = 0;
  let handNearMouth = false;

  try {
    const lms = landmarks as any[] | undefined;
    const getLm = (idx: number) => {
      if (!lms) return null;
      const byIndex = lms.find && lms.find((p: any) => (p.i ?? -1) === idx);
      if (byIndex) return byIndex;
      return lms[idx] ?? null;
    };

    const lipL = getLm(61);
    const lipR = getLm(291);
    const lipLower = getLm(17);
    const upperLip = getLm(13);
    const lowerLip = getLm(14);
    const nose = getLm(1) || getLm(10);
    const chin = getLm(152);

    if (lipL && lipR && lipLower && upperLip && lowerLip && nose && chin && lipL.y != null && lipR.y != null && lipLower.y != null && upperLip.y != null && lowerLip.y != null && nose.y != null && chin.y != null) {
      const midY = (lipL.y + lipR.y) / 2;
      const faceScale = Math.max(0.001, Math.sqrt((chin.x - nose.x) ** 2 + (chin.y - nose.y) ** 2));
      const d = lipLower.y - midY;
      const lowerLipCurve = (-d) / faceScale;
      // Compensate mild upward head tilt so pitch alone does not inflate sadness.
      const pitchUp = Math.max(0, headRotation?.pitch ?? 0);
      const pitchExcess = Math.max(0, pitchUp - 0.06);
      const sadPosePenalty = Math.min(0.045, pitchExcess * 0.35);
      lipCurveSad = Math.max(0, lowerLipCurve - sadPosePenalty);
      lipCurveSmile = Math.max(0, -lowerLipCurve);
    }

    const mouthUpperLeft = getLm(78);
    const mouthUpperRight = getLm(308);
    const mouthLowerLeft = getLm(82);
    const mouthLowerRight = getLm(312);

    if (upperLip && lowerLip && mouthUpperLeft && mouthUpperRight && mouthLowerLeft && mouthLowerRight && lipL && lipR) {
      const v1 = dist(upperLip, lowerLip);
      const v2 = dist(mouthUpperLeft, mouthLowerLeft);
      const v3 = dist(mouthUpperRight, mouthLowerRight);
      const h = dist(lipL, lipR);
      if (h > 0) mar = (v1 + v2 + v3) / (2 * h);
    }

    const eyeLeftTop = getLm(159);
    const eyeLeftBottom = getLm(145);
    const eyeRightTop = getLm(386);
    const eyeRightBottom = getLm(374);
    const eyeLeftLeft = getLm(33);
    const eyeLeftRight = getLm(133);
    const eyeRightLeft = getLm(362);
    const eyeRightRight = getLm(263);

    if (eyeLeftTop && eyeLeftBottom && eyeLeftLeft && eyeLeftRight && eyeRightTop && eyeRightBottom && eyeRightLeft && eyeRightRight) {
      const leftEAR = dist(eyeLeftTop, eyeLeftBottom) / dist(eyeLeftLeft, eyeLeftRight);
      const rightEAR = dist(eyeRightTop, eyeRightBottom) / dist(eyeRightLeft, eyeRightRight);
      ear = (leftEAR + rightEAR) / 2;
    }

    const mouthCenterPoints = [getLm(13), getLm(14), getLm(78), getLm(308)];
    const validMouthPoints = mouthCenterPoints.filter(p => p && p.x != null && p.y != null) as { x: number; y: number }[];

    if (validMouthPoints.length === 4 && handLandmarks && handLandmarks.length > 0) {
      const mouthCenter = {
        x: validMouthPoints.reduce((s, p) => s + p.x, 0) / 4,
        y: validMouthPoints.reduce((s, p) => s + p.y, 0) / 4,
      };
      let distanceMin = Number.POSITIVE_INFINITY;
      for (const h of handLandmarks) {
        if (h && typeof h.x === 'number' && typeof h.y === 'number') {
          distanceMin = Math.min(distanceMin, dist(mouthCenter, h));
        }
      }
      // Relax threshold for real-world hand positioning and noise
      handNearMouth = distanceMin < 0.10;
    }

    if (blendshapes) (blendshapes as any).__handNearMouth = handNearMouth;
  } catch (err) {
    lipCurveSmile = 0;
    lipCurveSad = 0;
    mar = 0;
    ear = 0;
    handNearMouth = false;
  }

  return { lipCurveSmile, lipCurveSad, mar, ear, handNearMouth };
};
